import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Paperclip, Send } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { QuotationRecord } from "@/types";

interface SendQuoteDialogProps {
  quote: QuotationRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SendQuoteDialog({ quote, open, onOpenChange, onSent }: SendQuoteDialogProps) {
  const { success, error } = useToast();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Prefill from the quote's contact each time the dialog opens
  useEffect(() => {
    if (!open || !quote) return;
    const contactName = quote.contact ? `${quote.contact.first_name} ${quote.contact.last_name || ""}`.trim() : "";
    const validUntil = new Date(`${quote.valid_until}T00:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    setTo(quote.sent_to_email || quote.contact?.email || "");
    setCc("");
    setSubject(`Quotation ${quote.display_number}`);
    setMessage(
      `Dear ${contactName || quote.account?.name || "Customer"},\n\n` +
        `Please find attached our quotation ${quote.display_number} for ${formatINR(quote.grand_total)}, valid until ${validUntil}.\n\n` +
        `Let us know if you have any questions. We look forward to working with you.`
    );
  }, [open, quote]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quote) return;
    const ccList = cc
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!EMAIL_RE.test(to.trim())) {
      error("Invalid Email", "Enter a valid recipient email");
      return;
    }
    const badCc = ccList.find((c) => !EMAIL_RE.test(c));
    if (badCc) {
      error("Invalid Email", `CC address "${badCc}" is not valid`);
      return;
    }

    setSending(true);
    try {
      const res: any = await api.post(`/api/Quotation/Send/${quote.id}`, {
        to: to.trim(),
        cc: ccList.length ? ccList : undefined,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      const simulated = String(res?.AddtionalData || "").toLowerCase().includes("simulated");
      const whatsapp = String(res?.Message || "").match(/(Also sent on WhatsApp|WhatsApp (failed|not sent).*)$/)?.[0];
      success(
        "Quote Sent",
        [simulated ? "SMTP is not configured, so the email was simulated (see server log)" : `Emailed to ${to.trim()}`, whatsapp]
          .filter(Boolean)
          .join(" · ")
      );
      onOpenChange(false);
      onSent();
    } catch (err: any) {
      error("Send Failed", err.message || "Could not send the quote");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Send className="size-5 text-primary" />
            Send Quotation
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">The quote PDF is attached automatically.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSend} className="space-y-3.5 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs sm:text-sm font-semibold">To *</Label>
            <Input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs sm:text-sm font-semibold">CC</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Separate multiple addresses with commas" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs sm:text-sm font-semibold">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs sm:text-sm font-semibold">Message</Label>
            <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground rounded-md bg-muted/40 px-3 py-2">
            <Paperclip className="size-4" />
            {quote?.display_number}.pdf
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={sending} className="gap-1.5">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
